import { Controller } from '@hotwired/stimulus'

/**
 * TurboEventLoggingController
 *
 * A dedicated Stimulus controller for logging all Turbo events.
 * This controller can be added to any element (typically the body)
 * to log all Turbo events to the console.
 */
export default class TurboEventLogging extends Controller {
  static values = {
    logLevel: { type: String, default: 'none' } // Possible values: 'debug', 'info', 'none'
  }
  connect() {
    this.formatters = {}
    console.debug(`TurboEventLogging: connected with log level ${this.logLevelValue}`)

    // Register event listeners for all Turbo events
    this.registerEventListeners()
  }

  disconnect() {
    console.debug('TurboEventLogging: disconnected')

    // Remove all event listeners
    this.removeEventListeners()
  }

  registerEventListeners() {
    if(this.logLevelValue === 'none') {
      return
    }
    // Navigation events
    document.addEventListener('turbo:click', this.logFormatter('info'))
    document.addEventListener('turbo:before-visit', this.logFormatter('info'))
    document.addEventListener('turbo:visit', this.logFormatter('info'))
    document.addEventListener('turbo:load', this.logFormatter('info'))

    // Page lifecycle events
    document.addEventListener('turbo:before-cache', this.logFormatter('info'))
    document.addEventListener('turbo:before-render', this.logFormatter('info'))
    document.addEventListener('turbo:render', this.logFormatter('info'))

    // Morph events
    document.addEventListener('turbo:morph', this.logFormatter('info'))

    if(this.logLevelValue === 'debug') {
      document.addEventListener('turbo:before-morph-element', this.logFormatter('debug'))
      document.addEventListener('turbo:before-morph-attribute', this.logFormatter('debug'))
      document.addEventListener('turbo:morph-element', this.logFormatter('debug'))
    }

    // Form events
    document.addEventListener('turbo:submit-start', this.logFormatter('info'))
    document.addEventListener('turbo:submit-end', this.logFormatter('info'))

    // Frame events
    document.addEventListener('turbo:before-frame-render', this.logFormatter('info'))
    document.addEventListener('turbo:frame-render', this.logFormatter('info'))
    document.addEventListener('turbo:frame-load', this.logFormatter('info'))
    document.addEventListener('turbo:frame-missing', this.logFormatter('info'))

    // Stream events
    document.addEventListener('turbo:before-stream-render', this.logFormatter('info'))

    // Custom turboRequest events (from turbo_helper.js)
    document.addEventListener('turboRequest:start', this.logFormatter('info'))
    document.addEventListener('turboRequest:beforeSend', this.logFormatter('info'))
    document.addEventListener('turboRequest:success', this.logFormatter('info'))
    document.addEventListener('turboRequest:failed', this.logFormatter('info'))

    // HTTP Request events
    document.addEventListener('turbo:before-fetch-request', this.logFormatter('info'))
    document.addEventListener('turbo:before-fetch-response', this.logFormatter('info'))
    document.addEventListener('turbo:before-prefetch', this.logFormatter('info'))
    document.addEventListener('turbo:fetch-request-error', this.logFormatter('info'))
  }

  removeEventListeners() {
    if(this.logLevelValue === 'none') {
      return
    }
    // Navigation events
    document.removeEventListener('turbo:click', this.logFormatter('info'))
    document.removeEventListener('turbo:before-visit', this.logFormatter('info'))
    document.removeEventListener('turbo:visit', this.logFormatter('info'))
    document.removeEventListener('turbo:load', this.logFormatter('info'))

    // Page lifecycle events
    document.removeEventListener('turbo:before-cache', this.logFormatter('info'))
    document.removeEventListener('turbo:before-render', this.logFormatter('info'))
    document.removeEventListener('turbo:render', this.logFormatter('info'))

    // Morph events
    document.removeEventListener('turbo:morph', this.logFormatter('info'))

    if(this.logLevelValue === 'debug') {
      document.removeEventListener('turbo:before-morph-element', this.logFormatter('debug'))
      document.removeEventListener('turbo:before-morph-attribute', this.logFormatter('debug'))
      document.removeEventListener('turbo:morph-element', this.logFormatter('debug'))
    }

    // Form events
    document.removeEventListener('turbo:submit-start', this.logFormatter('info'))
    document.removeEventListener('turbo:submit-end', this.logFormatter('info'))

    // Frame events
    document.removeEventListener('turbo:before-frame-render', this.logFormatter('info'))
    document.removeEventListener('turbo:frame-render', this.logFormatter('info'))
    document.removeEventListener('turbo:frame-load', this.logFormatter('info'))
    document.removeEventListener('turbo:frame-missing', this.logFormatter('info'))

    // Stream events
    document.removeEventListener('turbo:before-stream-render', this.logFormatter('info'))

    // Custom turboRequest events (from turbo_helper.js)
    document.removeEventListener('turboRequest:start', this.logFormatter('info'))
    document.removeEventListener('turboRequest:beforeSend', this.logFormatter('info'))
    document.removeEventListener('turboRequest:success', this.logFormatter('info'))
    document.removeEventListener('turboRequest:failed', this.logFormatter('info'))

    // HTTP Request events
    document.removeEventListener('turbo:before-fetch-request', this.logFormatter('info'))
    document.removeEventListener('turbo:before-fetch-response', this.logFormatter('info'))
    document.removeEventListener('turbo:before-prefetch', this.logFormatter('info'))
    document.removeEventListener('turbo:fetch-request-error', this.logFormatter)
  }

  logFormatter = (logLevel = 'info') => {
    if(this.formatters[logLevel]) {
      return this.formatters[logLevel]
    }

    this.formatters[logLevel] = (event) => {
      const eventType = event.type
      const eventTarget = event.target
      const targetInfo = eventTarget.id
        ? `#${eventTarget.id}`
        : eventTarget.className
        ? `.${eventTarget.className.replace(/ /g, '.')}`
        : eventTarget.tagName

      console[logLevel](
        `%c[Turbo Event] %c${eventType} %c${targetInfo}`,
        'color: blue; font-weight: bold',
        'color: green; font-weight: bold',
        'color: gray'
      )

      // Log additional details for specific events if they have detail
      if (event.detail) {
        console[logLevel]('  Detail:', event.detail)
      }
    }

    return this.formatters[logLevel]
  }
}

export { TurboEventLogging }
